import {
  CyoPriceStrategy,
  Draft,
  DraftConfiguration,
  FulfillmentPriceStrategy,
  MdsPriceStrategy,
  MpPriceStrategy,
  PriceStrategy,
  TeamshirtPriceStrategy,
} from '@/types';
import { assertNever } from '@/utils/assertNever';
import {
  isDraftDesignConfiguration,
  isDraftSpecialTextConfiguration,
} from '@/model/configuration/isDraftConfigurationType';
import { isFreeDesign } from '@/model/design/isFreeDesign';
import { isUploadedDesign } from '@/model/design/isUploadedDesign';
import { getConfigurations } from '@/model/draft/getConfigurations';
import { getProductType } from '@/model/draft/getProductType';
import { getPrintColor } from '@/model/printType/getPrintColor';
import { isDigital } from '@/model/printType/isDigital';
import { getContext } from '@/redux/core/getters/getContext';
import { StoreState } from '@/redux/types';
import { convertPriceIfNeeded } from './convertPriceIfNeeded';
import { isOwnerOfShopAndDesign } from './isOwnerOfShopAndDesign';

const collectPrintAreas = (
  configurations: DraftConfiguration[],
  mappingFunction: (id: string, index: number) => PrintAreaPrice
) => {
  return Array.from(new Set(configurations.map(({ printArea: { id } }) => id)), mappingFunction);
};

const mapConfiguration = (configuration: DraftConfiguration) => {
  let designId: string | undefined;
  let text: string | undefined;
  let icon: string | undefined;

  if (isDraftDesignConfiguration(configuration)) {
    designId = configuration.design?.id;
  } else {
    text = configuration.text.content;
  }

  if (isDraftSpecialTextConfiguration(configuration)) {
    icon = configuration.text.style.font.icon;
  }

  return {
    id: configuration.cid,
    type: configuration.type,
    designId,
    text,
    icon,
    price: 0,
  };
};

const getPrintAreaPricesTeamshirt = (priceStrategy: TeamshirtPriceStrategy, draft: Draft) => {
  const { firstPrintAreaCosts, additionalPrintAreaCosts } = priceStrategy.settings;
  const configurations = getConfigurations(draft);
  const areas = collectPrintAreas(configurations, (id, index) => ({
    id,
    printing: index === 0 ? firstPrintAreaCosts : additionalPrintAreaCosts,
    configurations: [],
  }));

  configurations.forEach((configuration) => {
    const area = areas.find((a) => a.id === configuration.printArea.id);
    if (area) {
      const mappedConfiguration = mapConfiguration(configuration);
      mappedConfiguration.price = 0;
      area.configurations.push(mappedConfiguration);
    }
  });
  return areas;
};

const getPrintAreaPricesFulfillment = (priceStrategy: FulfillmentPriceStrategy, draft: Draft) => {
  const { printAreaPrice } = priceStrategy.settings;
  const configurations = getConfigurations(draft);
  const areas = collectPrintAreas(configurations, (id, index) => ({
    id,
    printing: index === 0 ? 0 : printAreaPrice,
    configurations: [],
  }));

  configurations.forEach((configuration) => {
    const area = areas.find((a) => a.id === configuration.printArea.id);
    if (area) {
      area.configurations.push(mapConfiguration(configuration));
    }
  });
  return areas;
};

const getPrintAreaPricesCyo = (priceStrategy: CyoPriceStrategy, draft: Draft) => {
  const productTypeId = getProductType(draft).id;
  const { cyoProductTypeBasedPrintAreaPrices, cyoSpecialFontCommission } = priceStrategy;
  const productTypeBasedPrintPrices =
    cyoProductTypeBasedPrintAreaPrices[productTypeId] ||
    cyoProductTypeBasedPrintAreaPrices['DEFAULT'];
  const configurations = getConfigurations(draft);

  const alreadyBilledPrintTypes = new Set();
  const areas = Array.from(
    configurations
      .filter(
        (configuration, index, configurationArr) =>
          configurationArr.findIndex((c) => c.printArea.id === configuration.printArea.id) === index
      )
      .map((configuration) => {
        let usedPrintTypeId = 'DEFAULT';
        if (
          productTypeBasedPrintPrices.cyoPrintTypeBasedPrintAreaPrices &&
          configuration.printType.id in productTypeBasedPrintPrices.cyoPrintTypeBasedPrintAreaPrices
        ) {
          usedPrintTypeId = configuration.printType.id;
        }
        const printAreaPrice =
          usedPrintTypeId == 'DEFAULT' ||
          productTypeBasedPrintPrices.cyoPrintTypeBasedPrintAreaPrices == null
            ? alreadyBilledPrintTypes.has(usedPrintTypeId)
              ? productTypeBasedPrintPrices.otherPrintAreasAmount
              : productTypeBasedPrintPrices.firstPrintAreaAmount
            : alreadyBilledPrintTypes.has(usedPrintTypeId)
            ? productTypeBasedPrintPrices.cyoPrintTypeBasedPrintAreaPrices[usedPrintTypeId]
                .otherPrintAreasAmount
            : productTypeBasedPrintPrices.cyoPrintTypeBasedPrintAreaPrices[usedPrintTypeId]
                .firstPrintAreaAmount;
        alreadyBilledPrintTypes.add(usedPrintTypeId);
        return {
          id: configuration.printArea.id,
          printing: printAreaPrice,
          configurations: [],
        } as PrintAreaPrice;
      })
  );

  const alreadyBilledDesigns = new Set();
  const alreadyBilledSpecialFonts = new Set();

  configurations.forEach((configuration) => {
    const area = areas.find((a) => a.id === configuration.printArea.id);
    if (area) {
      const mappedConfiguration = mapConfiguration(configuration);

      if (isDraftDesignConfiguration(configuration)) {
        const design = configuration.design;

        if (!alreadyBilledDesigns.has(design.id)) {
          if (!isFreeDesign(design) && !isUploadedDesign(design)) {
            mappedConfiguration.price = priceStrategy.designBasedCommission.amount;
          }

          alreadyBilledDesigns.add(design.id);
        }
      }

      if (isDraftSpecialTextConfiguration(configuration) && cyoSpecialFontCommission) {
        const specialText = configuration.text.style.font;

        if (!alreadyBilledSpecialFonts.has(specialText.id)) {
          mappedConfiguration.price = cyoSpecialFontCommission.amount;
        }
        alreadyBilledSpecialFonts.add(specialText.id);
      }

      area.configurations.push(mappedConfiguration);
    }
  });
  return areas;
};

const getMdsPrintAreaPrice = (
  priceStrategy: MdsPriceStrategy,
  productTypeId: string,
  state: StoreState
) => {
  let printAreaPrice = 0;
  if (priceStrategy.productTypeBasedPrintAreaPrices) {
    const priceObj =
      priceStrategy.productTypeBasedPrintAreaPrices[productTypeId] ||
      priceStrategy.productTypeBasedPrintAreaPrices['DEFAULT'];
    printAreaPrice = priceObj.amount;
  } else {
    throw new Error('No productTypeBasedPrintAreaPrices set on priceStrategy!');
  }

  return convertPriceIfNeeded(printAreaPrice, state);
};

const getPrintAreaPricesMds = (
  priceStrategy: MdsPriceStrategy,
  draft: Draft,
  state: StoreState
) => {
  const productTypeId = getProductType(draft).id;
  const configurations = getConfigurations(draft);
  const shop = getContext(state);

  const areas = collectPrintAreas(configurations, (id) => ({
    id,
    printing: getMdsPrintAreaPrice(priceStrategy, productTypeId, state),
    configurations: [],
  }));

  const alreadyBilledDesigns = new Set();

  configurations.forEach((configuration) => {
    const area = areas.find((a) => a.id === configuration.printArea.id);
    if (area) {
      const mappedConfiguration = mapConfiguration(configuration);

      if (isDraftDesignConfiguration(configuration)) {
        const design = configuration.design;

        if (!alreadyBilledDesigns.has(design.id)) {
          if (
            !isFreeDesign(design) &&
            !isUploadedDesign(design) &&
            !isOwnerOfShopAndDesign(shop, design)
          ) {
            mappedConfiguration.price = convertPriceIfNeeded(
              priceStrategy.designBasedCommission.amount,
              state
            );
          }

          alreadyBilledDesigns.add(design.id);
        }
      }

      area.configurations.push(mappedConfiguration);
    }
  });

  return areas;
};

const getMpPrintPrice = (configuration: DraftConfiguration) => {
  const printType = configuration.printType;
  const printTypePrice = printType.price?.vatIncluded || 0;

  if (isDigital(printType)) {
    return printTypePrice;
  }

  const uniquePrintColors = new Set<string>();
  let printColorPrice = 0;

  configuration.printColors.forEach(({ id }) => {
    if (id && !uniquePrintColors.has(id)) {
      uniquePrintColors.add(id);

      const color = getPrintColor(printType, id);
      if (color) {
        printColorPrice += color.price.vatIncluded;
      }
    }
  });

  return printTypePrice + printColorPrice;
};

const getPrintAreaPricesMp = (priceStrategy: MpPriceStrategy, draft: Draft) => {
  const productTypeId = getProductType(draft).id;
  const configurations = getConfigurations(draft);
  const areas = collectPrintAreas(configurations, (id) => ({
    id,
    printing: 0,
    configurations: [],
  }));

  const alreadyBilledDesigns = new Set();

  configurations.forEach((configuration) => {
    const area = areas.find((a) => a.id === configuration.printArea.id);
    if (area) {
      const mappedConfiguration = mapConfiguration(configuration);

      if (isDraftDesignConfiguration(configuration)) {
        const design = configuration.design;

        if (!alreadyBilledDesigns.has(design.id)) {
          if (!isFreeDesign(design) && !isUploadedDesign(design)) {
            const { productTypeBasedCommissions } = priceStrategy;
            mappedConfiguration.price = (
              productTypeBasedCommissions[productTypeId] || productTypeBasedCommissions.DEFAULT
            ).amount;
          }

          alreadyBilledDesigns.add(design.id);
        }
      }

      area.printing += getMpPrintPrice(configuration);
      area.configurations.push(mappedConfiguration);
    }
  });

  return areas;
};

export const getPrintAreaPrices = (
  priceStrategy: PriceStrategy,
  draft: Draft,
  state: StoreState
) => {
  switch (priceStrategy.type) {
    case 'TeamshirtPriceStrategy':
      return getPrintAreaPricesTeamshirt(priceStrategy, draft);
    case 'FulfillmentPriceStrategy':
      return getPrintAreaPricesFulfillment(priceStrategy, draft);
    case 'CyoPriceStrategy':
      return getPrintAreaPricesCyo(priceStrategy, draft);
    case 'MdsPriceStrategy':
      return getPrintAreaPricesMds(priceStrategy, draft, state);
    case 'MpPriceStrategy':
      return getPrintAreaPricesMp(priceStrategy, draft);
  }

  assertNever(priceStrategy);
};
